/**
 * Browser Navigate Command
 * 
 * NOTE: This command is also registered in aiCommands.ts for AI tool use.
 * The AI version includes formatViewData and is rendered via BrowserNavigateRenderer.
 */

import type { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { entityService } from '../../../services/entityService';
import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('BROWSER_COMMANDS');

export const browserNavigateCommand: SelfContainedCommand = {
  id: 'browser_navigate',
  description: 'Navigate a BrowserPage (by tab) to a new URL and update its history.',
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  schema: {
    parameters: [
      { name: 'windowEntityId', type: 'string', description: 'BrowserWindow entity id', required: true },
      { name: 'tabId', type: 'string', description: 'Tab id within the window', required: false },
      { name: 'url', type: 'string', description: 'Destination URL', required: true },
    ]
  },
  examples: { good: [], bad: [] },
  performance: { expectedDuration: 200, complexity: 'low' },
  handler: async (params: any, _context: CommandExecutionContext) => {
    try {
      const { windowEntityId, tabId, url } = params;
      const win = await entityService.get(windowEntityId);
      const tabs = (win?.skeleton?.tabs || []) as Array<any>;
      const activeTabId = tabId || win?.skeleton?.activeTabId;
      const tabRef = tabs.find((t: any) => t.id === activeTabId) || tabs[0];
      if (!tabRef) return createErrorResult('No tabs found for window', 'validation');

      const pageId = tabRef.pageId;
      const page = await entityService.get(pageId);
      const history = page?.skeleton?.history || { entries: [], index: -1 };
      const newEntries = history.entries.slice(0, history.index + 1).concat(url);
      const newIndex = newEntries.length - 1;

      await entityService.update(pageId, {
        skeleton: {
          ...(page.skeleton || {}),
          url,
          history: { entries: newEntries, index: newIndex },
          lastVisitedAt: new Date().toISOString()
        }
      });

      return createSuccessResult('Navigated', { windowEntityId, tabId: tabRef.id, pageId, url });
    } catch (error: any) {
      log.error('[browser_navigate] Error:', error);
      return createErrorResult('Failed to navigate', error?.message || 'Unknown error');
    }
  }
};

export default browserNavigateCommand;


